import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Loader2, Sparkles } from 'lucide-react';
import { INDUSTRIES, TIMEZONE_OPTIONS } from './businessSettingsConstants';
import type { IBusinessProfileEditFormProps } from '@/@types/businessSettings';

export function BusinessProfileEditForm({
  formData,
  setFormData,
  isSaving,
  isGenerating,
  onSave,
  onCancel,
  onGenerate,
}: IBusinessProfileEditFormProps) {
  return (
    <div className="space-y-4 rounded-lg border p-6">
      <h2 className="text-xl font-semibold mb-4">Edit Business Profile</h2>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Business Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., ABC Real Estate Agency"
            disabled={isSaving}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="brandName">Brand Name (Optional)</Label>
          <Input
            id="brandName"
            value={formData.brandName}
            onChange={e => setFormData({ ...formData, brandName: e.target.value })}
            placeholder="e.g., ABC Realty"
            disabled={isSaving}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="industry">Industry *</Label>
          <Select
            value={formData.industry}
            onValueChange={value =>
              setFormData({
                ...formData,
                industry: value,
                customIndustry: value === 'other' ? formData.customIndustry : '',
              })
            }
            disabled={isSaving}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select your industry" />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRIES.map(industry => (
                <SelectItem key={industry.value} value={industry.value}>
                  {industry.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {formData.industry === 'other' && (
          <div className="space-y-2">
            <Label htmlFor="customIndustry">Your Industry *</Label>
            <Input
              id="customIndustry"
              value={formData.customIndustry}
              onChange={e => setFormData({ ...formData, customIndustry: e.target.value })}
              placeholder="e.g., Photography, Event Planning"
              disabled={isSaving}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="websiteUrl">Website URL (Optional)</Label>
          <Input
            id="websiteUrl"
            type="url"
            value={formData.websiteUrl}
            onChange={e => setFormData({ ...formData, websiteUrl: e.target.value })}
            placeholder="example.com"
            disabled={isSaving}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Business Description (Optional)</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe your business..."
            rows={4}
            disabled={isSaving}
          />
          <p className="text-xs text-muted-foreground">
            This description is used for AI content generation.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="timezone">Timezone</Label>
          <Select
            value={formData.timezone}
            onValueChange={value => setFormData({ ...formData, timezone: value })}
            disabled={isSaving}
          >
            <SelectTrigger id="timezone" className="w-full">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONE_OPTIONS.map(tz => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Used for follow-up scheduling</p>
        </div>
      </div>

      <div className="flex justify-between gap-2 pt-4">
        <Button
          variant="outline"
          onClick={onGenerate}
          disabled={!formData.websiteUrl.trim() || isSaving || isGenerating}
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate description
            </>
          )}
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isSaving || isGenerating}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaving || isGenerating}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
